'use client';

import { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DialogProps } from './types';
import CloseIcon from '../Icons/CloseIcon';
import { useMounted } from '@/lib/hooks/useMounted';

export default function Dialog({
  open,
  onClose,
  children,
  stickyFooter,
  maxWidth = 'max-w-sm',
  className = '',
  backButton,
  ...props
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const mounted = useMounted();

  /* ---- sync the native <dialog> open state with the `open` prop ---- */
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    if (open && !el.open) {
      el.showModal(); // opens as a top-layer modal (with native backdrop)
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  /* ---- close on native cancel (Escape key) ---- */
  const handleCancel = useCallback(
    (e: React.SyntheticEvent<HTMLDialogElement>) => {
      e.preventDefault(); // prevent the browser from closing it directly
      onClose();
    },
    [onClose],
  );

  /* ---- close on backdrop (click on ::backdrop / <dialog> itself) ---- */
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current) {
        onClose();
      }
    },
    [onClose],
  );

  /* ---- lock body scroll while open ---- */
  useEffect(() => {
    if (!open) return;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [open]);

  // Don't render portal until mounted (prevents hydration mismatch)
  if (!mounted) return null;

  return createPortal(
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      aria-modal='true'
      className={[
        'fixed inset-y-0 right-0 z-50 p-0 ml-auto h-dvh max-h-screen',
        `w-[calc(100%-2rem)] ${maxWidth}`,
        'bg-white',
        'backdrop:bg-darkgray/50 backdrop:backdrop-blur-xs',
        'translate-x-full transition-[translate,opacity] duration-300 ease-out motion-reduce:transition-none',
        'open:translate-x-0',
        className,
      ].join(' ')}
      {...props}
    >
      <div className='flex h-full flex-col'>
        <div className='border-b border-border-global p-4 flex items-center justify-end'>
          {backButton ? (
            backButton
          ) : (
            <button
              onClick={onClose}
              aria-label='Close modal'
              className='flex cursor-pointer items-center justify-center border-2 text-brand-icon size-10 rounded-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500 outline-none'
            >
              <CloseIcon className='text-primary-500 size-10' />
            </button>
          )}
        </div>
        <div className='flex-1 overflow-y-auto overscroll-contain p-6'>{children}</div>
        {stickyFooter && <footer className='sticky bottom-0'>{stickyFooter}</footer>}
      </div>
    </dialog>,
    document.body,
  );
}
